import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import {
  ErrorCode,
  displayRightsDashboardPaths,
  type ApiErrorResponse,
  type ApiResponse,
  type ReviseDisplayRightsProposalRequest,
  type SendDisplayRightsProposalRequest,
  type SendDisplayRightsProposalResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type DisplayRightsError = AxiosError<ApiErrorResponse>;

/**
 * Every surface that shows a display-rights state, by query-key prefix: the
 * mention panel, the contact card and the co-publicites list all embed the same
 * live summary, so one send moves three views. The composition context goes too
 * — the gesture it resolved is exactly what just changed.
 */
const AFFECTED_QUERY_PREFIXES: QueryKey[] = [
  ['mentionBoostEligibility'],
  ['mentionBoostSetup'],
  ['contact-ads'],
  ['coPublicites'],
  ['displayRightsComposition'],
];

export function useRefreshDisplayRightsSurfaces({
  includeComposition = true,
}: { includeComposition?: boolean } = {}) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    for (const queryKey of AFFECTED_QUERY_PREFIXES) {
      if (!includeComposition && queryKey[0] === 'displayRightsComposition') {
        continue;
      }
      queryClient.invalidateQueries({ queryKey });
    }
  }, [includeComposition, queryClient]);
}

/**
 * Refusals that mean "the screen you clicked on is out of date": the creator
 * answered, the window closed, or the authorization was withdrawn between the
 * render and the click.
 *
 * These are the only errors worth refetching on. The message already tells the
 * brand what happened (every one of these codes is translated en/fr); the
 * refresh is what stops the stale affordance from inviting a second click.
 */
const STALE_STATE_ERROR_CODES = new Set<string>([
  ErrorCode.E_DISPLAY_RIGHTS_PROPOSAL_CONFLICT,
  ErrorCode.E_DISPLAY_RIGHTS_INVALID_TRANSITION,
  ErrorCode.E_DISPLAY_RIGHTS_PROPOSAL_EXPIRED,
  ErrorCode.E_DISPLAY_RIGHTS_PROPOSAL_SUPERSEDED,
  ErrorCode.E_DISPLAY_RIGHTS_PROPOSAL_NOT_FOUND,
]);

/**
 * One error handler for the three gestures: name what happened, and when the
 * refusal says the screen is stale, make the screen catch up.
 */
function useDisplayRightsErrorHandler() {
  const { t } = useTranslation();
  const refreshSurfaces = useRefreshDisplayRightsSurfaces();

  return useCallback(
    (error: DisplayRightsError) => {
      toaster.error({
        title: t('common.error'),
        description: getApiErrorMessage(error, t, 'common.error'),
      });

      const code = error.response?.data?.code;
      if (code && STALE_STATE_ERROR_CODES.has(code)) {
        refreshSurfaces();
      }
    },
    [refreshSurfaces, t]
  );
}

const postSendProposal = async (
  payload: SendDisplayRightsProposalRequest
): Promise<SendDisplayRightsProposalResponse> => {
  const response = await api.post<ApiResponse<SendDisplayRightsProposalResponse>>(
    `/dashboard${displayRightsDashboardPaths({}).send}`,
    payload
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'The proposal could not be sent.');
  }

  return response.data.data;
};

interface ReviseProposalVariables extends ReviseDisplayRightsProposalRequest {
  proposalId: string;
}

const postReviseProposal = async ({
  proposalId,
  ...payload
}: ReviseProposalVariables): Promise<SendDisplayRightsProposalResponse> => {
  const response = await api.post<ApiResponse<SendDisplayRightsProposalResponse>>(
    `/dashboard${displayRightsDashboardPaths({ proposalId }).revision}`,
    payload
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'The proposal could not be replaced.');
  }

  return response.data.data;
};

const postRemindProposal = async ({
  proposalId,
}: {
  proposalId: string;
}): Promise<SendDisplayRightsProposalResponse> => {
  const response = await api.post<ApiResponse<SendDisplayRightsProposalResponse>>(
    `/dashboard${displayRightsDashboardPaths({ proposalId }).reminder}`
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'The reminder could not be sent.');
  }

  return response.data.data;
};

/**
 * Compose and deliver the first version of a chain. Also the endpoint of a
 * renewal (a renewal is a brand new chain, never a re-opened one) and of a
 * residual draft the server adopts instead of refusing.
 */
export const useSendDisplayRightsProposal = () => {
  const refreshSurfaces = useRefreshDisplayRightsSurfaces();
  const handleError = useDisplayRightsErrorHandler();

  return useMutation<
    SendDisplayRightsProposalResponse,
    DisplayRightsError,
    SendDisplayRightsProposalRequest
  >({
    mutationFn: (input) => trackProductAction('rights', 'send', () => postSendProposal(input)),
    onSuccess: refreshSurfaces,
    onError: handleError,
  });
};

/**
 * Replace terms the creator has not answered yet. The old version becomes
 * definitively un-acceptable and the new one gets its own link, so the explicit
 * confirmation belongs upstream of this call.
 */
export const useReviseDisplayRightsProposal = () => {
  const refreshSurfaces = useRefreshDisplayRightsSurfaces();
  const handleError = useDisplayRightsErrorHandler();

  return useMutation<
    SendDisplayRightsProposalResponse,
    DisplayRightsError,
    ReviseProposalVariables
  >({
    mutationFn: (input) => trackProductAction('rights', 'update', () => postReviseProposal(input)),
    onSuccess: refreshSurfaces,
    onError: handleError,
  });
};

/**
 * Send the same proposal again from the connected mailbox. Nothing about the
 * version changes — not its terms, not its window — so the only feedback is a
 * toast; the surrounding state is already on screen.
 */
export const useRemindDisplayRightsProposal = () => {
  const { t } = useTranslation();
  const handleError = useDisplayRightsErrorHandler();

  return useMutation<SendDisplayRightsProposalResponse, DisplayRightsError, { proposalId: string }>(
    {
      mutationFn: (input) => trackProductAction('rights', 'send', () => postRemindProposal(input)),
      // No invalidation: a reminder changes nothing server-side — not the terms,
      // not the version, not the window — so re-reading five query families
      // would fetch bit-identical data. A refusal still refreshes, through the
      // shared error handler, because that one means the screen was stale.
      onSuccess: () => {
        toaster.success({ title: t('displayRights.actions.remindSuccess') });
      },
      onError: handleError,
    }
  );
};
